import {NxLoader} from '@nextbank/ui-components';
import isEmpty from 'lodash/isEmpty';
import React, {createContext, useContext, useEffect, useState} from 'react';
import {useParams} from 'react-router';
import {useLocation} from 'react-router-dom';
import {MANAGEMENT_BRANCHES_URL, MANAGEMENT_ROLES_URL} from '../../../constants/api-urls';
import {UrlParams} from '../../../routes/routes.model';
import useGet from '../../../shared/hooks/use-get.hook';
import {useLoanProcess} from '../../../shared/hooks/use-loan-process.hook';
import useSingleQuery from '../../../shared/hooks/use-single-query.hook';
import {Branch, Role} from '../../../shared/model/management.model';
import {ApiHelper} from '../../../utils/api-helper';
import {isSysAdminOrManager} from '../../../utils/permissions-utils';
import {NEW_LOAN_APPLICATION_ID} from '../../dashboard/routes/dashboard-routes';
import {AppSnackbarContext} from '../../shared/app-snackbar-provider/AppSnackbarProvider';
import LoanStepWrapper from '../../shared/loan-step-wrapper/LoanStepWrapper';
import RenderRoutes from '../../shared/render-routes/RenderRoutes';
import {LoanApplicationContextType} from './loan-application-context.model';
import LoanApplicationSidebar from './loan-application-sidebar/LoanApplicationSidebar';
import LoanApplicationTopBar from './loan-application-topbar/LoanApplicationTopBar';
import {Application, ApplicationStatus} from './loan-application.model';
import {LoanNavigation} from './navigation/LoanNavigation';
import {LOAN_APPLICATION_ROUTES} from './routes/loan-application-routes';
import {LoanApplicationSteps} from './steps/loan-application-steps';
import {SecurityContext} from '../../../App';

export const LoanApplicationContext =
  createContext<LoanApplicationContextType>({} as LoanApplicationContextType);

const LoanApplication = (): React.ReactElement => {

  const {userData} = useContext(SecurityContext);
  const roles = useSingleQuery<Role[]>(MANAGEMENT_ROLES_URL);
  const branches = useSingleQuery<Branch[]>(MANAGEMENT_BRANCHES_URL);
  const {showErrorMessage} = useContext(AppSnackbarContext);
  const {processId, applicationId} = useParams<UrlParams>();
  const {process} = useLoanProcess(processId);
  const getApplication = useGet<Application>(ApiHelper.getApplicationUrl(applicationId));
  const [application, setApplication] = useState<Application>();
  const [processChanged, setProcessChanged] = useState(false);

  const path = useLocation().pathname;

  const [steps, setSteps] = useState<LoanApplicationSteps>(
    new LoanApplicationSteps(processId, applicationId, [])
  );
  const isStepDone = steps.getIsStepAlreadyDone(path, application?.currentPhaseId);
  const isUserAssignee = application?.assignedTo === userData?.id;
  const isUserCreator = application?.createdBy === userData?.id;

  useEffect(() => {
    setSteps(new LoanApplicationSteps(processId, applicationId, []));
  }, [processId]);

  const refreshApplication = (): Promise<void> => getApplication()
    .then(application => setApplication(application))
    .catch(error => showErrorMessage(error.message));

  useEffect(() => {
    // for new applications applicationId will have value 'new'
    if (isNaN(Number(applicationId))) {
      return;
    }

    refreshApplication();
  }, []);

  useEffect(() => {
    setSteps(new LoanApplicationSteps(processId, applicationId, process?.phases));
  }, [process.phases, applicationId]);

  const isDeferred = application?.status === ApplicationStatus.DEFERRED;

  const applicationContext = {
    roles,
    branches,
    application,
    applicationParams: {
      isEditable:
        application?.status !== ApplicationStatus.SUBMITTED &&
        application?.status !== ApplicationStatus.CANCELED &&
        !isDeferred &&
        (isSysAdminOrManager(userData) || isUserAssignee || isUserCreator),
      isSubmitted: application?.status === ApplicationStatus.SUBMITTED,
      isPending: application?.status === ApplicationStatus.PENDING,
      isDeferred,
      isStepDone
    },
    setApplication,
    refreshApplication,
    processId: Number(processId),
    process,
    steps,
    applicationChanged: false,
    processChanged,
    setProcessChanged
  };

  return isEmpty(process) ? <NxLoader /> :
    <LoanApplicationContext.Provider value={applicationContext}>
        <LoanNavigation>
          <LoanStepWrapper content={<RenderRoutes routes={LOAN_APPLICATION_ROUTES} />}
                           topBar={<LoanApplicationTopBar productName={process.loanProduct.name} />}
                           aside={<LoanApplicationSidebar disabled={applicationId === NEW_LOAN_APPLICATION_ID}
                                                          currentPhaseId={application?.currentPhaseId}
                                                          steps={steps} />} />
        </LoanNavigation>
    </LoanApplicationContext.Provider>;
};

export default LoanApplication;
